import { LocaleIdentifier } from '@repo/markets/types'
import { LinkType } from '@repo/sanity/shorthand'
import { cn } from '@repo/ui/lib/utils'
import { createHrefForLink } from '@repo/url/href'
import { LinkProps } from 'next/dist/client/link'
import Link from 'next/link'
import { ComponentPropsWithoutRef, FunctionComponent, PropsWithChildren } from 'react'

interface Props extends ComponentPropsWithoutRef<'a'> {
  link: LinkType
  locale: LocaleIdentifier
  scroll?: LinkProps['scroll']
  onNavigate?: LinkProps['onNavigate']
  className?: string
}

const LinkResolver: FunctionComponent<PropsWithChildren<Props>> = ({ link, locale, scroll, onNavigate, className, children, ...rest }) => {
  const { title, href } = createHrefForLink(link, locale)

  if (href) {
    const target = link?.newTab ? '_blank' : undefined
    const isExternal = typeof href === 'string' && /^https?:\/\//.test(href)

    // Prevent CORS issue for preload of non-self domain.
    const rel = link?.newTab || isExternal ? 'noopener noreferrer' : undefined

    // Certain libraries, such as Elevar, add URL params to external URLs and therefore introduce hydration mismatches. Whilst true, nothing is amiss.
    const suppressHydrationWarning = isExternal

    return (
      <Link
        href={href}
        title={title ?? undefined}
        className={cn(className)}
        onNavigate={onNavigate}
        scroll={scroll}
        target={target}
        rel={rel}
        suppressHydrationWarning={suppressHydrationWarning}
        {...rest}
      >
        {children}
      </Link>
    )
  }

  return <>{children}</>
}

export default LinkResolver
